import { useEffect, useState } from "react";
import { client } from "../../lib/config";
import { toast } from "sonner";
import { Sparkles, Copy, Check, RefreshCw, Trash2 } from "lucide-react";

function nextMonday() {
  const d = new Date();
  const day = d.getDay();
  const diff = (8 - day) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

export default function AdminGBPPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [weekStart, setWeekStart] = useState(nextMonday());
  const [count, setCount] = useState(4);
  const [editingId, setEditingId] = useState(null);
  const [editingCopy, setEditingCopy] = useState("");

  const reload = () => {
    setLoading(true);
    client.get("/admin/gbp-posts").then((r) => setPosts(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await client.post("/admin/gbp-posts/generate", { week_start: weekStart, count });
      toast.success(`Generated ${r.data.count} posts`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const regen = async (post) => {
    try {
      await client.post(`/admin/gbp-posts/${post.id}/regenerate`);
      toast.success("Regenerated");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regenerate failed");
    }
  };

  const markPosted = async (post) => {
    await client.put(`/admin/gbp-posts/${post.id}`, { posted: !post.posted });
    reload();
  };

  const saveEdit = async () => {
    await client.put(`/admin/gbp-posts/${editingId}`, { copy: editingCopy });
    setEditingId(null);
    reload();
    toast.success("Saved");
  };

  const remove = async (post) => {
    if (!window.confirm("Delete this post?")) return;
    await client.delete(`/admin/gbp-posts/${post.id}`);
    reload();
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
  };

  return (
    <>
      <div className="mb-6">
        <div className="wb-eyebrow">Marketing</div>
        <h1 className="wb-h2 mt-2">GBP post calendar</h1>
        <p className="wb-body mt-2 max-w-2xl">
          AI-generated Google Business Profile posts (Claude Sonnet 4.6) drawing from your published blog posts and this month's events. Warm, local voice — 100–180 words each.
        </p>
      </div>

      <div className="wb-card bg-white p-6 mb-6 flex flex-wrap items-end gap-4" data-testid="gbp-generate-form">
        <div>
          <label className="text-xs font-bold text-[#4A4641] uppercase tracking-wider">Starting week</label>
          <input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="block mt-1 px-3 py-2 border border-[#E5DFD3] rounded-sm" data-testid="gbp-week-start" />
        </div>
        <div>
          <label className="text-xs font-bold text-[#4A4641] uppercase tracking-wider">How many weeks</label>
          <input type="number" min={1} max={12} value={count} onChange={(e) => setCount(Number(e.target.value))} className="block mt-1 px-3 py-2 border border-[#E5DFD3] rounded-sm w-24" data-testid="gbp-count" />
        </div>
        <button onClick={generate} disabled={generating} className="wb-btn-primary" data-testid="gbp-generate-btn">
          <Sparkles className="h-4 w-4" /> {generating ? "Generating…" : `Generate ${count} posts`}
        </button>
      </div>

      {loading ? (
        <div className="wb-card bg-white p-12 text-center text-[#4A4641]">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="wb-card bg-white p-12 text-center text-[#4A4641]">No posts yet. Generate your first week above.</div>
      ) : (
        <div className="space-y-4" data-testid="gbp-posts-list">
          {posts.map((p) => (
            <article key={p.id} className={`wb-card bg-white p-6 ${p.posted ? "border-l-4 border-l-green-500" : ""}`} data-testid={`gbp-post-${p.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="wb-eyebrow">Week of {p.week_start}</div>
                  <div className="text-xs text-[#4A4641] mt-1">Photo hint: <code>{p.photo_hint}</code>{p.posted && p.posted_at ? ` · posted ${p.posted_at.slice(0, 10)}` : ""}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => copyText(p.copy, p.id)} className="wb-btn-outline text-sm py-1.5" data-testid={`gbp-copy-${p.id}`}>
                    <Copy className="h-4 w-4" /> Copy
                  </button>
                  <button onClick={() => markPosted(p)} className="wb-btn-outline text-sm py-1.5" data-testid={`gbp-toggle-posted-${p.id}`}>
                    <Check className="h-4 w-4" /> {p.posted ? "Unmark" : "Mark posted"}
                  </button>
                  <button onClick={() => regen(p)} className="wb-btn-outline text-sm py-1.5" data-testid={`gbp-regen-${p.id}`}>
                    <RefreshCw className="h-4 w-4" /> Regenerate
                  </button>
                  <button onClick={() => remove(p)} className="wb-btn-outline text-sm py-1.5 border-[#B93D12] text-[#B93D12]" data-testid={`gbp-delete-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {editingId === p.id ? (
                <div className="mt-4">
                  <textarea value={editingCopy} onChange={(e) => setEditingCopy(e.target.value)} rows={8} className="w-full px-3 py-2 border border-[#E5DFD3] rounded-sm text-sm" data-testid={`gbp-edit-textarea-${p.id}`} />
                  <div className="mt-3 flex gap-2">
                    <button onClick={saveEdit} className="wb-btn-primary text-sm py-1.5" data-testid={`gbp-save-${p.id}`}>Save</button>
                    <button onClick={() => setEditingId(null)} className="wb-btn-ghost text-sm py-1.5">Cancel</button>
                  </div>
                </div>
              ) : (
                <p onClick={() => { setEditingId(p.id); setEditingCopy(p.copy); }} className="mt-4 whitespace-pre-line text-[#1A1A1A] leading-relaxed cursor-text hover:bg-[#FDFBF7] p-3 -mx-3 rounded-sm">
                  {p.copy}
                </p>
              )}
              <div className="mt-2 text-xs text-[#4A4641]">
                Word count: {p.copy.trim().split(/\s+/).length}
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
